'use client';

import clsx from 'clsx';
import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { Menu, X } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useState } from 'react';

import { Button } from '@/slw/shared/ui/button';
import { AuthModal } from '@/slw/widgets/auth/auth.modal';
import { NotificationDropdown } from '@/slw/widgets/notification/notification-dropdown';
import { HomeFilterType } from '@/slw/entities/home/home.model';
import { useHomeFilterQuery } from '@/slw/entities/search-results/search-results.query';
import { useGlobalStore } from '@/slw/shared/stores';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/slw/shared/ui/navigation-menu';

import { UserDropdownMenu } from './user-dropdown-menu.ui';

export function Header({ isLoggedIn }: { isLoggedIn: boolean }) {
  const [openMenu, setOpenMenu] = useState(false);
  const pathname = usePathname();
  const { isAuthModalOpen, openAuthModal, closeAuthModal } = useGlobalStore();

  const homeFilterQuery = useHomeFilterQuery();
  const homeFilter = homeFilterQuery?.data?.data || ({} as HomeFilterType);

  const isHeaderTransparent = ['/'].includes(pathname);
  const isHeaderTransparentOpacity = ['/available-dealer'].includes(pathname);
  const linkBaseClasses = 'whitespace-nowrap text-base hover:text-primary duration-300 transition-all py-1';

  return (
    <header
      className={clsx('py-2 md:py-6', {
        'bg-foreground': !isHeaderTransparent && !isHeaderTransparentOpacity,
        'bg-transparent absolute w-full top-0 z-50': isHeaderTransparent && !isHeaderTransparentOpacity,
        'bg-foreground/70 absolute w-full top-0 z-50': isHeaderTransparentOpacity,
      })}
    >
      <div className='container'>
        <div className='relative flex items-center justify-between'>
          <div className='md:hidden'>
            <button type='button' className='border-none p-0' onClick={() => setOpenMenu(true)}>
              <Menu size={28} strokeWidth={1.75} color='#fff' />
            </button>
          </div>
          <div
            className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 md:relative logo max-h-[40px]
          md:max-h-[56px] flex-shrink-0 md:left-0 md:top-[unset] md:translate-none'
          >
            <Link href='/' title='Clovis Classics'>
              <Image
                className='h-[40px] w-auto md:h-[56px]'
                src='/images/logo/logo.png'
                alt='Clovis Classics'
                width={100}
                height={57}
                priority
                quality={100}
              />
            </Link>
          </div>
          {openMenu && (
            <button
              type='button'
              className='fixed outline-0 border-none w-screen h-screen left-0 top-0 md:hidden'
              onClick={() => setOpenMenu(false)}
            />
          )}
          <nav
            className={clsx(
              `menu w-4/5 -left-[100vh] top-0 min-h-screen shadow-xl fixed z-50 bg-default/95 p-5 transition-all
          md:absolute md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:bg-transparent md-p-0
          md:w-[unset] md:min-h-[unset] md:shadow-none duration-500`,
              {
                'left-0': openMenu,
              },
            )}
          >
            {openMenu && (
              <>
                <button
                  type='button'
                  className='absolute outline-0 border-none right-2 top-2 md:hidden z-10 text-background'
                  onClick={() => setOpenMenu(false)}
                >
                  <X size={28} strokeWidth={1.75} />
                </button>
              </>
            )}

            <NavigationMenu viewport={false}>
              <NavigationMenuList
                className='navigation-menu-main list-none flex flex-col items-start md:items-center
              md:flex-row gap-4 md:gap-10'
              >
                <NavigationMenuItem>
                  <Link
                    href='/'
                    title=''
                    className={clsx(linkBaseClasses, 'text-background', {
                      'text-primary font-bold': pathname === '/',
                    })}
                    onClick={() => setOpenMenu(false)}
                  >
                    Home
                  </Link>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger
                    className={clsx(linkBaseClasses, 'cursor-pointer select-none text-background', {
                      'text-primary font-bold': !!['/search-results/'].find((item) => pathname.startsWith(item)),
                    })}
                  >
                    Browse Inventory
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className='hidden md:block'>
                    <ul className='grid w-[200px] gap-4'>
                      <li>
                        {(homeFilter.vehicleEra || []).map((item, idx) => (
                          <NavigationMenuLink asChild key={idx}>
                            <Link
                              href={`/search-results/all-year?filterByTag=${item.code}&defaultFt=1`}
                              className='font-medium'
                            >
                              {item.name}
                            </Link>
                          </NavigationMenuLink>
                        ))}

                        <NavigationMenuLink asChild>
                          <Link href='/search-results/all-year?defaultFt=1' className='font-medium'>
                            Browse all
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    </ul>
                  </NavigationMenuContent>

                  <ul className='md:hidden flex flex-col gap-3 pl-5 mt-2'>
                    {(homeFilter.vehicleEra || []).map((item, idx) => (
                      <NavigationMenuItem key={idx}>
                        <Link
                          href={`/search-results/all-year?filterByTag=${item.code}&defaultFt=1`}
                          className={clsx(linkBaseClasses, 'text-sm', {
                            'text-background': true,
                          })}
                          onClick={() => setOpenMenu(false)}
                        >
                          {item.name}
                        </Link>
                      </NavigationMenuItem>
                    ))}

                    <NavigationMenuItem>
                      <Link
                        href='/search-results/all-year?defaultFt=1'
                        className={clsx(linkBaseClasses, 'text-sm', {
                          'text-background': true,
                        })}
                        onClick={() => setOpenMenu(false)}
                      >
                        Browse all
                      </Link>
                    </NavigationMenuItem>
                  </ul>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link
                    href='/sell-your-car'
                    title=''
                    className={clsx(linkBaseClasses, 'text-background', {
                      'text-primary font-bold': pathname === '/sell-your-car',
                    })}
                    onClick={() => setOpenMenu(false)}
                  >
                    Sell Your Car
                  </Link>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link
                    href='/available-dealer'
                    title=''
                    className={clsx(linkBaseClasses, 'text-background', {
                      'text-primary font-bold':
                        pathname.startsWith('/available-dealer') || pathname.startsWith('/dealer-listings/'),
                    })}
                    onClick={() => setOpenMenu(false)}
                  >
                    Find Dealers
                  </Link>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger
                    className={clsx(linkBaseClasses, 'cursor-pointer select-none text-background', {
                      'text-primary font-bold': !![
                        '/terms-and-conditions',
                        '/about-us',
                        '/contact-us',
                        '/help-center',
                        '/privacy-policy',
                        '/become-dealer-partner',
                      ].find((item) => pathname === item),
                    })}
                  >
                    About Us
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className='hidden md:block'>
                    <ul className='grid w-[200px] gap-4'>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link href='/terms-and-conditions' className='font-medium'>
                            Terms and Conditions
                          </Link>
                        </NavigationMenuLink>
                        <NavigationMenuLink asChild>
                          <Link href='/about-us' className='font-medium'>
                            About Us
                          </Link>
                        </NavigationMenuLink>
                        <NavigationMenuLink asChild>
                          <Link href='/contact-us' className='font-medium'>
                            Contact Us
                          </Link>
                        </NavigationMenuLink>
                        <NavigationMenuLink asChild>
                          <Link href='/help-center' className='font-medium'>
                            Help Center
                          </Link>
                        </NavigationMenuLink>
                        <NavigationMenuLink asChild>
                          <Link href='/privacy-policy' className='font-medium'>
                            Privacy Policy
                          </Link>
                        </NavigationMenuLink>
                        <NavigationMenuLink asChild>
                          <Link href='/become-dealer-partner' className='font-medium'>
                            Become a Dealer Partner
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    </ul>
                  </NavigationMenuContent>

                  <ul className='md:hidden flex flex-col gap-3 pl-5 mt-2'>
                    <NavigationMenuItem>
                      <Link
                        href='/terms-and-conditions'
                        className={clsx(linkBaseClasses, 'text-sm', {
                          'text-background': true,
                        })}
                        onClick={() => setOpenMenu(false)}
                      >
                        Terms and Conditions
                      </Link>
                    </NavigationMenuItem>
                    <NavigationMenuItem>
                      <Link
                        href='/about-us'
                        className={clsx(linkBaseClasses, 'text-sm', {
                          'text-background': true,
                        })}
                        onClick={() => setOpenMenu(false)}
                      >
                        About Us
                      </Link>
                    </NavigationMenuItem>
                    <NavigationMenuItem>
                      <Link
                        href='/contact-us'
                        className={clsx(linkBaseClasses, 'text-sm', {
                          'text-background': true,
                        })}
                        onClick={() => setOpenMenu(false)}
                      >
                        Contact Us
                      </Link>
                    </NavigationMenuItem>
                    <NavigationMenuItem>
                      <Link
                        href='/help-center'
                        className={clsx(linkBaseClasses, 'text-sm', {
                          'text-background': true,
                        })}
                        onClick={() => setOpenMenu(false)}
                      >
                        Help Center
                      </Link>
                    </NavigationMenuItem>
                    <NavigationMenuItem>
                      <Link
                        href='/privacy-policy'
                        className={clsx(linkBaseClasses, 'text-sm', {
                          'text-background': true,
                        })}
                        onClick={() => setOpenMenu(false)}
                      >
                        Privacy Policy
                      </Link>
                    </NavigationMenuItem>
                    <NavigationMenuItem>
                      <Link
                        href='/become-dealer-partner'
                        className={clsx(linkBaseClasses, 'text-sm', {
                          'text-background': true,
                        })}
                        onClick={() => setOpenMenu(false)}
                      >
                        Become a Dealer Partner
                      </Link>
                    </NavigationMenuItem>
                  </ul>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {isLoggedIn && (
              <div className='md:hidden flex pt-5 gap-5 mt-5 border-t border-white/20'>
                <Link href='/favorites' onClick={() => setOpenMenu(false)}>
                  <Image src='/images/common/favorite.png' alt='' width={22} height={21} priority />
                </Link>

                <motion.button
                  type='button'
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className='md:visible'
                  onClick={() => setOpenMenu(false)}
                >
                  <Image src='/images/common/notice.png' alt='' width={20} height={22} priority />
                </motion.button>
              </div>
            )}
          </nav>

          <div className='flex items-center gap-4'>
            {isLoggedIn && (
              <>
                <Link href='/favorites' className='hidden md:inline-block' onClick={() => setOpenMenu(false)}>
                  <Image src='/images/common/favorite.png' alt='' width={22} height={21} priority />
                </Link>
                <NotificationDropdown />
              </>
            )}
            {!isLoggedIn && (
              <Button
                type='button'
                variant='ghost'
                color='primary'
                className='cursor-pointer duration-300 transition-all py-1 px-0 outline-none! text-background'
                onClick={() => {
                  setOpenMenu(false);
                  openAuthModal();
                }}
              >
                Sign Up/Log In
              </Button>
            )}
            {isLoggedIn && <UserDropdownMenu isLoggedIn={isLoggedIn} />}
          </div>
        </div>
      </div>
      <AuthModal open={isAuthModalOpen} onClose={closeAuthModal} />
    </header>
  );
}
